import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import "./App.css";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { RedirectIfAuthed, RequireRole } from "./components/RouteGuards";
import { ClientLayout } from "./components/layout/ClientLayout";
import { AdminLayout } from "./components/layout/AdminLayout";

import Login from "./pages/Login";
import AdminLogin from "./pages/AdminLogin";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import NotFound from "./pages/NotFound";

import Dashboard from "./pages/client/Dashboard";
import Investments from "./pages/client/Investments";
import Withdrawals from "./pages/client/Withdrawals";
import Notifications from "./pages/client/Notifications";
import Statement from "./pages/client/Statement";
import Profile from "./pages/client/Profile";

import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminClients from "./pages/admin/AdminClients";
import AdminClientNew from "./pages/admin/AdminClientNew";
import AdminClientDetail from "./pages/admin/AdminClientDetail";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminSettings from "./pages/admin/AdminSettings";

const SessionWatcher = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => {
      const wasAdmin = user?.role === "admin";
      logout();
      toast.error("Session expired. Please sign in again.", { id: "session-expired" });
      navigate(wasAdmin ? "/admin/login" : "/login", { replace: true });
    };
    window.addEventListener("invexting:session-expired", handler);
    return () => window.removeEventListener("invexting:session-expired", handler);
  }, [logout, navigate, user]);

  return null;
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <SessionWatcher />
          <Toaster />
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/login"
              element={
                <RedirectIfAuthed>
                  <Login />
                </RedirectIfAuthed>
              }
            />
            <Route
              path="/admin"
              element={<Navigate to="/admin/login" replace />}
            />
            <Route
              path="/admin/login"
              element={
                <RedirectIfAuthed>
                  <AdminLogin />
                </RedirectIfAuthed>
              }
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            <Route
              element={
                <RequireRole role="client">
                  <ClientLayout />
                </RequireRole>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/investments" element={<Investments />} />
              <Route path="/withdrawals" element={<Withdrawals />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/statement" element={<Statement />} />
              <Route path="/profile" element={<Profile />} />
            </Route>

            <Route
              element={
                <RequireRole role="admin">
                  <AdminLayout />
                </RequireRole>
              }
            >
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/clients" element={<AdminClients />} />
              <Route path="/admin/clients/new" element={<AdminClientNew />} />
              <Route path="/admin/clients/:id" element={<AdminClientDetail />} />
              <Route path="/admin/notifications" element={<AdminNotifications />} />
              <Route path="/admin/audit" element={<AdminAudit />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
